import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Button, Loader, Stepper, Typography } from '@sm-dashboard/ui-components';
import instanceAxios from '@/util/axios';
import { uploadFileUrl } from '@/constants';
import { AiFreeTextInput } from './AiFreeTextInput';

interface AiFillFromAdProps {
  isExtracting: boolean;
  onUploaded: (imageUrl: string) => void;
  onFreeText: (text: string) => void;
}

/**
 * Dedicated AI-fill entry: the shaliach uploads the event ad (or pastes its text)
 * and the form fields are filled automatically. Separate from the regular event-image upload.
 */
export const AiFillFromAd = ({ isExtracting, onUploaded, onFreeText }: AiFillFromAdProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [showFreeText, setShowFreeText] = useState(false);
  const [source, setSource] = useState<'image' | 'text'>('image');

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    setSource('image');
    setUploadError(false);
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await instanceAxios.post(uploadFileUrl, formData);
      if (data?.publicUrl) {
        onUploaded(data.publicUrl);
      } else {
        setUploadError(true);
      }
    } catch {
      setUploadError(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFreeTextSubmit = (text: string) => {
    setSource('text');
    setUploadError(false);
    onFreeText(text);
  };

  const busy = isUploading || isExtracting;

  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px dashed',
        borderColor: 'primary.main',
        borderRadius: 1,
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box'
      }}
    >
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', minWidth: 0 }}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            loading={busy && source === 'image'}
            disabled={busy}
            startIcon={<AutoAwesomeIcon />}
            onClick={() => inputRef.current?.click()}
          >
            מילוי הפרטים באמצעות AI
          </Button>
          <Button
            variant={showFreeText ? 'contained' : 'outlined'}
            color="primary"
            fullWidth
            loading={busy && source === 'text'}
            disabled={busy}
            onClick={() => setShowFreeText(show => !show)}
          >
            מילוי מטקסט חופשי
          </Button>
        </Box>
        {busy ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Loader size={16} />
            <Typography variant="body">
              {isUploading
                ? 'מעלה את המודעה...'
                : source === 'text'
                  ? 'ה-AI קורא את הטקסט ומחלץ את הפרטים...'
                  : 'ה-AI קורא את המודעה ומחלץ את הפרטים...'}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body">
            {uploadError
              ? 'העלאת המודעה נכשלה, נסו שוב'
              : 'העלו את מודעת האירוע או הדביקו את הטקסט שלה והפרטים ימולאו אוטומטית'}
          </Typography>
        )}
      </Box>
      {/* Kept mounted while busy so the pasted text survives a failed extraction. */}
      {showFreeText && <AiFreeTextInput busy={busy} onSubmit={handleFreeTextSubmit} />}
      {busy && (
        <Box sx={{ mt: 2 }}>
          {source === 'text' ? (
            <Stepper steps={['חילוץ הפרטים', 'מילוי הטופס']} activeStep={0} />
          ) : (
            <Stepper steps={['העלאת המודעה', 'חילוץ הפרטים', 'מילוי הטופס']} activeStep={isUploading ? 0 : 1} />
          )}
        </Box>
      )}
    </Box>
  );
};
